/* eslint-disable max-lines */
import LinkLookup from 'components/LinkLookup';
import Redacted from 'components/Redacted';
import moment from 'moment';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { getCustomerValidEmailDomains, getCustomerZendeskDomain } from 'selectors/auth';
import { getSelectedCommunication } from 'selectors/communications';
import { getCommsFullMessageHighlighted } from 'selectors/events';

import UserEmail from 'components/User/UserEmail';
import { ATTACHMENT_ICON } from 'constants/envelopeIcons';
import { SectionType } from 'reducers/envelopeReview';
import getSelectedEnvelope, { getSelectedSection } from 'selectors/envelopeReview';
import {
  getBccRecipientsFromCommunication,
  getCcRecipientsFromCommunication,
  getEnvelope,
  getEnvelopeRecipientsAggs,
  getRecipientsFromCommunication,
  getToRecipientsFromCommunication,
} from 'selectors/envelopes';
import { getRecipientFilter } from 'selectors/envelopeView';
import { useSelector } from 'store';
import { Communication } from 'types';
import { isFileCommunication } from 'utils/communications';
import { extractDomain } from 'utils/userStrings';
import HighlightedSentenceComponent from './HighlightedSentence';

type ComponentProps = {
  renderSubject?: boolean;
};

const getRecipientHighlight = (
  recipient: Communication['recipients'][0],
  filters: string[],
  validDomains: string[],
  recipientsAggs: {
    recipient: string;
    higherRiskCount: number;
    afterHoursCount: number;
    offRadarCount: number;
  }[]
): boolean => {
  if (filters.length === 0) return false;
  const domain = extractDomain(recipient.id);

  if (
    filters.includes('external-domains') &&
    (!domain || (domain && validDomains.includes(domain)))
  ) {
    return false;
  }

  if (
    filters.includes('after-hours') &&
    (!recipientsAggs.find((rec) => rec.recipient === recipient.user_uuid) ||
      (recipientsAggs.find((rec) => rec.recipient === recipient.user_uuid) &&
        recipientsAggs.find((rec) => rec.recipient === recipient.user_uuid)?.afterHoursCount === 0))
  ) {
    return false;
  }

  if (
    filters.includes('off-radar') &&
    (!recipientsAggs.find((rec) => rec.recipient === recipient.user_uuid) ||
      (recipientsAggs.find((rec) => rec.recipient === recipient.user_uuid) &&
        recipientsAggs.find((rec) => rec.recipient === recipient.user_uuid)?.offRadarCount === 0))
  ) {
    return false;
  }

  if (
    filters.includes('higher-risk') &&
    (!recipientsAggs.find((rec) => rec.recipient === recipient.user_uuid) ||
      (recipientsAggs.find((rec) => rec.recipient === recipient.user_uuid) &&
        recipientsAggs.find((rec) => rec.recipient === recipient.user_uuid)?.higherRiskCount === 0))
  ) {
    return false;
  }

  return true;
};

const EnvelopeMessageInfo: React.FC<ComponentProps> = ({ renderSubject = true }) => {
  const sections: SectionType[] = ['message', 'history', 'attachment'];
  const { envelopeId } = useParams<{ envelopeId: string }>();
  const selectedEnvelope = useSelector(getSelectedEnvelope);

  const envelope = useSelector((state) => getEnvelope(state, selectedEnvelope?.uuid ?? envelopeId));
  const zendeskDomain = useSelector(getCustomerZendeskDomain);

  const recipients = useSelector(getRecipientsFromCommunication);
  const recipientsTo = useSelector(getToRecipientsFromCommunication);
  const recipientsCc = useSelector(getCcRecipientsFromCommunication);
  const recipientsBcc = useSelector(getBccRecipientsFromCommunication);
  const recipientsAggs = useSelector(getEnvelopeRecipientsAggs);

  const communication = useSelector(getSelectedCommunication);
  const selectedSection = useSelector(getSelectedSection);
  const recipientsFilter = useSelector(getRecipientFilter);
  const validDomains = useSelector(getCustomerValidEmailDomains);

  const isTeamsComm = communication?.platform === 'o365_teams';

  const filteredEvents = communication?.events
    ? communication.events.filter((event) => !event.hidden)
    : [];
  const eventId = filteredEvents && filteredEvents[0] ? filteredEvents[0].uuid : '';
  const res = useSelector((state) =>
    getCommsFullMessageHighlighted(state, communication?.uuid || '', 'false', 'false')
  );

  const [showMore, setShowMore] = useState(false);
  const [showMoreTo, setShowMoreTo] = useState(false);
  const [showMoreCC, setShowMoreCC] = useState(false);
  const [showMoreBCC, setShowMoreBCC] = useState(false);
  const [clampedSubject, setClampedSubject] = useState(false);
  const [clampedTo, setClampedTo] = useState(false);
  const [clampedCC, setClampedCC] = useState(false);
  const [clampedBCC, setClampedBCC] = useState(false);

  const setClampedRef = (
    node: HTMLDivElement,
    refValue: boolean,
    setRef: React.Dispatch<React.SetStateAction<boolean>>
  ): void => {
    if (node && !refValue) {
      const newC = node.scrollHeight > node.clientHeight + 2;
      setRef(newC);
    }
  };

  const getDate = (date: string): string => {
    const fixedDate = date.charAt(date.length - 1) !== 'Z' ? date.concat('Z') : date;
    const eventDate = moment(fixedDate).utc().local();
    return eventDate.format('MMM DD hh:mm A');
  };

  const subjectElement = (): JSX.Element | null => {
    if (renderSubject) {
      return (
        <div className="flex flex-row items-center gap-2">
          <div className="flex flex-row gap-1">
            <span className="font-bold">
              {envelope.platform === 'o365_teams' ? 'Channel: ' : 'Subject: '}
            </span>
            {res && res.length > 0 && res[0].type === 'subject' ? (
              <div className="flex flex-row">
                <div
                  ref={(node): void => {
                    if (node) {
                      setClampedRef(node, clampedSubject, setClampedSubject);
                    }
                  }}
                  className={`break-all ${showMore ? '' : 'clamped-sender'}`}
                >
                  <HighlightedSentenceComponent
                    eventId={eventId}
                    commUuid={communication?.uuid || ''}
                    fullMessageOptions="HIGHLIGHTED"
                    hideHighlight={!sections.includes(selectedSection)}
                    onlySubject
                  />
                </div>
                {clampedSubject && recipients !== null && (
                  <button
                    type="button"
                    data-testid="show-all-button"
                    className=" ml-2 whitespace-no-wrap underline text-litlingo-primary focus:outline-none"
                    onClick={(): void => {
                      setShowMore((b) => !b);
                    }}
                  >
                    {showMore ? 'Show Less' : 'Show More'}
                  </button>
                )}
              </div>
            ) : (
              <span>{communication?.subject}</span>
            )}
          </div>
          {communication?.meta_data?.attachment_list &&
            communication.meta_data.attachment_list.length > 0 &&
            communication?.communication_type !== 'attachment' && (
              <div className="w-4"> {ATTACHMENT_ICON} </div>
            )}
        </div>
      );
    }
    return null;
  };

  return (
    <>
      <div className="flex flex-col gap-1 text-body " id="envelope-content">
        {!isFileCommunication(communication) && (
          <div className="flex flex-col gap-1 overflow-hidden">
            <div className="flex flex-row gap-1 items-baseline">
              <span className="font-bold">From: </span>

              <Redacted field="communication_envelopes.created_by">
                {envelope.created_by && (
                  <UserEmail
                    userName={envelope.created_by.name || ''}
                    userEmail={envelope.created_by.email || ''}
                    userId={envelope.created_by.uuid}
                    highlight={getRecipientHighlight(
                      {
                        display_name: envelope.created_by.name,
                        id: envelope.created_by.email || '',
                        role: 'from',
                        type: '',
                        user_uuid: envelope.created_by.uuid,
                      },
                      recipientsFilter,
                      validDomains,
                      recipientsAggs
                    )}
                  />
                )}
              </Redacted>
            </div>

            <div className="flex flex-row gap-1 items-baseline">
              <span className="font-bold">To:</span>
              {recipientsTo && recipientsTo.length > 0 && envelope.platform !== 'zendesk' ? (
                <Redacted field="communications.recipients">
                  <div
                    ref={(node): void => {
                      if (node) {
                        setClampedRef(node, clampedTo, setClampedTo);
                      }
                    }}
                    className={`flex flex-row gap-1 flex-wrap ${
                      showMoreTo ? '' : 'overflow-hidden h-7'
                    }`}
                  >
                    {recipientsTo.map((recipient) => (
                      <UserEmail
                        key={recipient.id}
                        userId={recipient.user_uuid || ''}
                        userEmail={recipient.id}
                        userName={recipient.display_name || ''}
                        isTeamsComm={isTeamsComm}
                        highlight={getRecipientHighlight(
                          recipient,
                          recipientsFilter,
                          validDomains,
                          recipientsAggs
                        )}
                      />
                    ))}
                  </div>
                  {clampedTo && (
                    <button
                      type="button"
                      data-testid="show-all-button"
                      className=" ml-2 whitespace-no-wrap underline text-litlingo-primary focus:outline-none"
                      onClick={(): void => {
                        setShowMoreTo((b) => !b);
                      }}
                    >
                      {showMoreTo ? 'Show Less' : 'Show More'}
                    </button>
                  )}
                </Redacted>
              ) : (
                'N/A'
              )}
            </div>

            {envelope.platform !== 'zendesk' && envelope.platform !== 'o365_teams' && (
              <div className="flex flex-row gap-1">
                <span className="font-bold">CC:</span>
                {recipientsCc && recipientsCc.length > 0 ? (
                  <Redacted field="communications.recipients">
                    <div
                      ref={(node): void => {
                        if (node) {
                          setClampedRef(node, clampedCC, setClampedCC);
                        }
                      }}
                      className={`flex flex-row gap-1 flex-wrap ${
                        showMoreCC ? '' : 'overflow-hidden h-7'
                      }`}
                    >
                      {recipientsCc.map((recipient) => (
                        <UserEmail
                          key={recipient.id}
                          userId={recipient.user_uuid || ''}
                          userEmail={recipient.id}
                          userName={recipient.display_name || ''}
                          isTeamsComm={isTeamsComm}
                          highlight={getRecipientHighlight(
                            recipient,
                            recipientsFilter,
                            validDomains,
                            recipientsAggs
                          )}
                        />
                      ))}
                    </div>
                    {clampedCC && (
                      <button
                        type="button"
                        data-testid="show-all-button"
                        className=" ml-2 whitespace-no-wrap underline text-litlingo-primary focus:outline-none"
                        onClick={(): void => {
                          setShowMoreCC((b) => !b);
                        }}
                      >
                        {showMoreCC ? 'Show Less' : 'Show More'}
                      </button>
                    )}
                  </Redacted>
                ) : (
                  'N/A'
                )}
              </div>
            )}

            {envelope.platform !== 'zendesk' && envelope.platform !== 'o365_teams' && (
              <div className="flex flex-row gap-1">
                <span className="font-bold">BCC:</span>
                {recipientsBcc && recipientsBcc.length > 0 ? (
                  <Redacted field="communications.recipients">
                    <div
                      ref={(node): void => {
                        if (node) {
                          setClampedRef(node, clampedBCC, setClampedBCC);
                        }
                      }}
                      className={`flex flex-row gap-1 flex-wrap ${
                        showMoreBCC ? '' : 'overflow-hidden h-7'
                      }`}
                    >
                      {recipientsBcc.map((recipient) => (
                        <UserEmail
                          key={recipient.id}
                          userId={recipient.user_uuid || ''}
                          userEmail={recipient.id}
                          userName={recipient.display_name || ''}
                          isTeamsComm={isTeamsComm}
                          highlight={getRecipientHighlight(
                            recipient,
                            recipientsFilter,
                            validDomains,
                            recipientsAggs
                          )}
                        />
                      ))}
                    </div>
                    {clampedBCC && (
                      <button
                        type="button"
                        data-testid="show-all-button"
                        className=" ml-2 whitespace-no-wrap underline text-litlingo-primary focus:outline-none"
                        onClick={(): void => {
                          setShowMoreBCC((b) => !b);
                        }}
                      >
                        {showMoreBCC ? 'Show Less' : 'Show More'}
                      </button>
                    )}
                  </Redacted>
                ) : (
                  'N/A'
                )}
              </div>
            )}

            <div className="flex flex-row gap-1">
              <span className="font-bold">Date:</span>
              <div>{getDate(communication?.sent_at || '')}</div>
            </div>
            {envelope.platform === 'slack' ? (
              <div className="flex flex-row gap-1">
                <span className="font-bold">Channel:</span>
                <span>
                  {envelope.communications &&
                    envelope.communications[0] &&
                    // @ts-ignore asda
                    `${envelope.communications[0].meta_data?.channel}`}
                </span>
              </div>
            ) : (
              subjectElement()
            )}
          </div>
        )}

        {envelope && envelope.platform === 'zendesk' && (
          /* zendeskDomain && */
          <div className="flex flex-row self-end gap-2 text-xss font-normal leading-4 text-litlingo-info">
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={`https://${zendeskDomain}.zendesk.com/agent/tickets/${envelope.platform_thread_guid}`}
            >
              <span className="underline cursor-pointer">View ticket</span>
            </a>
            <LinkLookup
              routeName="envelope-list"
              target="_blank"
              queryParams={{
                envelopes__platform_thread_guid: envelope.platform_thread_guid,
              }}
            >
              <span className="underline cursor-pointer">View thread</span>
            </LinkLookup>
          </div>
        )}
      </div>
    </>
  );
};

export default EnvelopeMessageInfo;
